/**
 * glanceView — maps normalized NewsData + GlanceMap → GlanceViewModel.
 *
 * CLIENT-SIDE (portable into the extension bundle — ADR-002 §5). No imports
 * from server-only modules. Pure; returns a new object.
 *
 * Glance grammar design decisions for news.headlines (documented + tested):
 *
 *   primary  : the headline phrase, bounded by GLANCE_LIMITS.PRIMARY_MAX_CHARS
 *              (80, D-018). History: the original ≤8 contract produced "South Af"
 *              on the live default board — the "topic signal"/kicker theory
 *              (show the lead word as a hook) did not survive contact with real
 *              headlines, whose first 8 chars are rarely a topic. D-018 ruling:
 *              text primaries carry the whole glanceable phrase; the RENDERER
 *              ellipsizes to the tile's actual width (honest CSS text-overflow),
 *              so what fits is decided by geometry, not a blind character count.
 *
 *   delta    : source name, ≤20 chars (where the headline came from —
 *              this is the most useful "trend context" for news: which outlet?)
 *              Null when glance.delta is null OR no source is available.
 *
 *   state    : always 'neutral' for news (news tiles do not have a semantic
 *              positive/negative/alert state in v1 — topic severity is not
 *              inferable from the category alone without NLP, which is Pro scope).
 *              glance.state null → always 'neutral'.
 *
 * Edge-case rulings:
 *   1. Empty headline → primary = '' (zero items, or GNews returned empty title).
 *   2. Empty source → delta is null (no source to display).
 *   3. itemCount=0 → headline='', source='', delta=null.
 *   4. Pathological headline → sliced at the 80-char bound (no ellipsis — GLANCE_LIMITS contract).
 *   5. Long source → sliced to 20 chars by the GLANCE_LIMITS central clamp.
 */

import type { GlanceMap } from 'briefpane/schema';
import type { GlanceState, GlanceViewModel } from 'briefpane/contracts';
import { GLANCE_LIMITS } from 'briefpane/contracts';
import type { NewsData } from './normalize.js';

/**
 * Map normalized NewsData + the tile's GlanceMap → GlanceViewModel.
 *
 * CLIENT-SIDE. Pure. Returns a new object every call (immutable).
 *
 * glance.state is intentionally ignored for news — topic severity cannot be
 * inferred from category alone without NLP (Pro scope). State is always neutral.
 *
 * @param data   normalized news metrics (from normalize())
 * @param glance validated GlanceMap from the tile config (delta used; state ignored)
 */
export function glanceView(data: NewsData, glance: GlanceMap): GlanceViewModel {
  // --- primary slot ---
  // The full headline phrase, bounded at the 80-char safety cap (D-018);
  // visual fit is the renderer's ellipsis — see module doc above.
  const primary = data.headline.slice(0, GLANCE_LIMITS.PRIMARY_MAX_CHARS);

  // --- delta slot ---
  // Source name provides useful context: "which outlet?" matches the glance grammar
  // role of "trend/delta" for news tiles (not a numeric delta, but the metadata
  // most useful at-a-glance alongside the headline snippet).
  // Null when: config suppresses delta, or no source available.
  let delta: string | null = null;
  if (glance.delta !== null && data.source.length > 0) {
    // clampGlanceViewModel will enforce DELTA_MAX_CHARS; we produce it naturally.
    delta = data.source;
  }

  // --- glance state ---
  // News tiles are always neutral in v1 (see module doc above).
  // glance.state is not used — news lacks a semantic positive/negative coloring
  // that can be derived from the category; NLP-based sentiment is a Pro feature.
  const glanceState: GlanceState = 'neutral';

  return {
    dataState: 'ok',
    glanceState,
    slots: {
      primary,
      delta,
    },
    label: null, // label comes from the tile config, not the adapter
    fetchedAt: null, // fetchedAt comes from the cache envelope
  };
}
